import { useEffect, useRef, useState } from "react";
import { Loader2, Send, Lock, UserCheck, CheckCircle2, MessageSquare, Clock, Star, Ticket, AlertTriangle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { StatCard } from "@/components/dashboard/DashboardShell";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const STATUS_STYLE = {
  new: "bg-blue-100 text-blue-700", waiting: "bg-amber-100 text-amber-700",
  assigned: "bg-indigo-100 text-indigo-700", active: "bg-emerald-100 text-emerald-700",
  escalated: "bg-red-100 text-red-700", resolved: "bg-slate-200 text-slate-600",
  open: "bg-blue-100 text-blue-700",
};
const fmtWait = (s) => `${String(Math.floor(s / 60)).padStart(2, "0")}:${String(s % 60).padStart(2, "0")}`;

export function SupportChats() {
  const { user } = useAuth();
  const [stats, setStats] = useState(null);
  const [queue, setQueue] = useState([]);
  const [status, setStatus] = useState("all");
  const [category, setCategory] = useState("all");
  const [active, setActive] = useState(null);
  const [messages, setMessages] = useState([]);
  const [text, setText] = useState("");
  const [internal, setInternal] = useState(false);
  const endRef = useRef(null);

  const loadQueue = async () => {
    const [{ data: s }, { data: q }] = await Promise.all([
      api.get("/support/stats"),
      api.get("/support/queue", { params: { status: status === "all" ? undefined : status, category: category === "all" ? undefined : category } }),
    ]);
    setStats(s); setQueue(q);
  };
  useEffect(() => { loadQueue(); const t = setInterval(loadQueue, 6000); return () => clearInterval(t); /* eslint-disable-next-line */ }, [status, category]);

  const loadMessages = async (id) => { const { data } = await api.get(`/conversations/${id}/messages`); setMessages(data); };
  useEffect(() => { if (active) { loadMessages(active.id); const t = setInterval(() => loadMessages(active.id), 5000); return () => clearInterval(t); } /* eslint-disable-next-line */ }, [active?.id]);
  useEffect(() => { endRef.current?.scrollIntoView({ behavior: "smooth" }); }, [messages]);

  const send = async () => {
    if (!text.trim()) return;
    const t = text; setText("");
    await api.post(`/conversations/${active.id}/messages`, { text: t, internal });
    loadMessages(active.id); loadQueue();
  };
  const assignMe = async () => { const { data } = await api.patch(`/conversations/${active.id}/assign`, {}); setActive(data); loadQueue(); toast.success("Assigned to you"); };
  const setConvStatus = async (st) => { const { data } = await api.patch(`/conversations/${active.id}/status`, { status: st }); setActive(data); loadQueue(); toast.success(`Marked ${st}`); };

  return (
    <div className="space-y-6">
      {stats && (
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-5">
          <StatCard icon={MessageSquare} label="Open chats" value={stats.open_chats} />
          <StatCard icon={Clock} label="Waiting chats" value={stats.waiting_chats} color="amber" />
          <StatCard icon={UserCheck} label="Active agents" value={stats.active_agents} color="emerald" />
          <StatCard icon={Star} label="Avg chat rating" value={stats.avg_chat_rating || "—"} color="amber" />
          <StatCard icon={Ticket} label="Unresolved tickets" value={stats.unresolved_tickets} color="navy" />
        </div>
      )}

      <div className="flex flex-wrap gap-2">
        <Select value={status} onValueChange={setStatus}><SelectTrigger className="w-40" data-testid="chat-status-filter"><SelectValue /></SelectTrigger><SelectContent>{["all", "new", "waiting", "assigned", "active", "escalated", "resolved"].map((s) => <SelectItem key={s} value={s}>{s === "all" ? "All statuses" : s}</SelectItem>)}</SelectContent></Select>
        <Select value={category} onValueChange={setCategory}><SelectTrigger className="w-52" data-testid="chat-category-filter"><SelectValue /></SelectTrigger><SelectContent><SelectItem value="all">All categories</SelectItem>{(stats?.categories || []).map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent></Select>
      </div>

      <div className="grid gap-4 lg:grid-cols-5">
        {/* Live Chat Operations table */}
        <div className="lg:col-span-3 overflow-hidden rounded-2xl border border-slate-200 bg-white">
          <div className="border-b border-slate-200 bg-slate-50 px-4 py-3"><h3 className="font-heading text-sm font-semibold text-navy">Live Chat Operations</h3></div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="text-left text-xs uppercase text-slate-500"><tr><th className="p-3">Customer</th><th className="p-3">Booking</th><th className="p-3">Agent</th><th className="p-3">Category</th><th className="p-3">Waiting</th><th className="p-3">Status</th><th className="p-3">Rating</th></tr></thead>
              <tbody>
                {queue.length === 0 ? <tr><td colSpan={7} className="p-6 text-center text-slate-400">No conversations in queue.</td></tr> : queue.map((c) => (
                  <tr key={c.id} onClick={() => setActive(c)} data-testid={`queue-row-${c.id}`} className={`cursor-pointer border-t border-slate-100 hover:bg-brand-light ${active?.id === c.id ? "bg-brand-light" : ""}`}>
                    <td className="p-3 font-medium text-navy">{c.customer_name}</td>
                    <td className="p-3 text-slate-500">{c.booking_ref || "—"}</td>
                    <td className="p-3 text-slate-500">{c.assigned_agent_name || "—"}</td>
                    <td className="p-3 text-slate-500">{c.category}</td>
                    <td className="p-3 font-mono text-xs">{c.status === "waiting" || c.status === "new" ? fmtWait(c.waiting_seconds || 0) : "—"}</td>
                    <td className="p-3"><span className={`rounded-full px-2 py-1 text-xs font-semibold ${STATUS_STYLE[c.status]}`}>{c.status}</span></td>
                    <td className="p-3 text-gold">{c.rating ? "★".repeat(c.rating) : "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* Chat panel */}
        <div className="lg:col-span-2 flex h-[520px] flex-col rounded-2xl border border-slate-200 bg-white">
          {active ? (
            <>
              <div className="border-b border-slate-200 p-4">
                <div className="flex items-center justify-between">
                  <div><p className="font-heading font-semibold text-navy">{active.customer_name}</p><p className="text-xs text-slate-500">{active.category}{active.booking_ref ? ` · ${active.booking_ref}` : ""}</p></div>
                  <span className={`rounded-full px-2 py-1 text-xs font-semibold ${STATUS_STYLE[active.status]}`}>{active.status}</span>
                </div>
                <div className="mt-3 flex flex-wrap gap-2">
                  <Button size="sm" variant="outline" onClick={assignMe} data-testid="assign-me"><UserCheck size={13} className="mr-1" /> Assign to me</Button>
                  <Button size="sm" variant="outline" onClick={() => setConvStatus("escalated")} data-testid="escalate-chat">Escalate</Button>
                  <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700" onClick={() => setConvStatus("resolved")} data-testid="resolve-chat"><CheckCircle2 size={13} className="mr-1" /> Resolve</Button>
                </div>
              </div>
              <div className="flex-1 space-y-3 overflow-auto p-4 no-scrollbar">
                {messages.map((m) => {
                  const mine = m.sender_id === user?.id;
                  return (
                    <div key={m.id} className={`flex ${mine ? "justify-end" : "justify-start"}`}>
                      <div className={`max-w-[80%] rounded-2xl px-3 py-2 text-sm ${m.internal ? "border border-amber-200 bg-amber-100 text-amber-900" : mine ? "bg-brand text-white" : "bg-slate-100 text-slate-800"}`}>
                        {m.internal && <span className="mb-0.5 flex items-center gap-1 text-[10px] font-semibold"><Lock size={9} /> Internal note</span>}
                        {!mine && !m.internal && <span className="mb-0.5 block text-[10px] font-semibold uppercase opacity-70">{m.sender_name}</span>}
                        {m.text}
                      </div>
                    </div>
                  );
                })}
                <div ref={endRef} />
              </div>
              <div className="border-t border-slate-200 p-3">
                <label className="mb-2 flex items-center gap-2 text-xs text-slate-500"><input type="checkbox" checked={internal} onChange={(e) => setInternal(e.target.checked)} data-testid="internal-toggle" /> Internal note (hidden from customer)</label>
                <div className="flex gap-2">
                  <Input value={text} onChange={(e) => setText(e.target.value)} onKeyDown={(e) => e.key === "Enter" && send()} placeholder="Type a reply…" data-testid="support-chat-input" />
                  <Button onClick={send} className="bg-brand hover:bg-brand/90" data-testid="support-chat-send"><Send size={16} /></Button>
                </div>
              </div>
            </>
          ) : <div className="flex flex-1 items-center justify-center text-sm text-slate-400">Select a conversation from the queue</div>}
        </div>
      </div>
    </div>
  );
}

export function SupportTickets() {
  const [rows, setRows] = useState(null);
  const [status, setStatus] = useState("all");
  const load = () => api.get("/support/tickets", { params: { status: status === "all" ? undefined : status } }).then(({ data }) => setRows(data));
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [status]);
  const upd = async (id, body) => { await api.patch(`/support/tickets/${id}`, body); toast.success("Ticket updated"); load(); };
  if (!rows) return <div className="flex h-64 items-center justify-center"><Loader2 className="animate-spin text-brand" size={28} /></div>;
  return (
    <div className="space-y-4">
      <Select value={status} onValueChange={setStatus}><SelectTrigger className="w-44" data-testid="ticket-status-filter"><SelectValue /></SelectTrigger><SelectContent>{["all", "open", "assigned", "waiting", "escalated", "resolved"].map((s) => <SelectItem key={s} value={s}>{s === "all" ? "All tickets" : s}</SelectItem>)}</SelectContent></Select>
      <div className="grid gap-3">
        {rows.length === 0 ? <p className="text-sm text-slate-500">No tickets.</p> : rows.map((t) => (
          <div key={t.id} className="rounded-xl border border-slate-200 bg-white p-4" data-testid={`ticket-${t.id}`}>
            <div className="flex flex-wrap items-start justify-between gap-2">
              <div>
                <div className="flex items-center gap-2">
                  <span className="font-semibold text-navy">{t.ticket_ref}</span>
                  {t.priority === "high" && <span className="flex items-center gap-1 rounded-full bg-red-100 px-2 py-0.5 text-xs font-semibold text-red-700"><AlertTriangle size={11} /> High</span>}
                  <span className={`rounded-full px-2 py-0.5 text-xs font-semibold ${STATUS_STYLE[t.status]}`}>{t.status}</span>
                </div>
                <p className="mt-1 text-sm text-navy">{t.subject}</p>
                <p className="text-xs text-slate-500">{t.category} · {t.customer_name}{t.booking_ref ? ` · ${t.booking_ref}` : ""}{t.assigned_agent_name ? ` · ${t.assigned_agent_name}` : ""}</p>
              </div>
            </div>
            <div className="mt-3 flex flex-wrap gap-2">
              <Button size="sm" variant="outline" onClick={() => upd(t.id, { status: "assigned", assigned_agent_name: "Me" })}>Assign</Button>
              <Button size="sm" variant="outline" onClick={() => upd(t.id, { status: "escalated" })}>Escalate</Button>
              <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700" onClick={() => upd(t.id, { status: "resolved" })} data-testid={`resolve-ticket-${t.id}`}>Resolve</Button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
